import { AlertDialog, Button, Text } from "@overmap-ai/blocks"
import { type Organization } from "@overmap-ai/common"
import { memo, useCallback, useMemo, useState } from "react"

import { useAppSelector } from "../../store"
import { selectActiveOrganization } from "../../store/selectors"

const dismissalKey = (organizationUuid: Organization["uuid"]): string => `dedicated-environment:${organizationUuid}`

// Browser storage can be unavailable (for example, in private browsing or when site data is blocked).
// In that case, the dialog asks again the next time the app opens.
const getIsDismissed = (organizationUuid: Organization["uuid"]): boolean => {
	try {
		return localStorage.getItem(dismissalKey(organizationUuid)) !== null
	} catch {
		return false
	}
}

const setIsDismissed = (organizationUuid: Organization["uuid"]): void => {
	try {
		localStorage.setItem(dismissalKey(organizationUuid), new Date().toISOString())
	} catch {
		// See `getIsDismissed`.
	}
}

const parseUrl = (url: string | null | undefined): URL | null => {
	if (!url) return null
	try {
		return new URL(url)
	} catch {
		return null
	}
}

/**
 * Offers members of an organization that has a dedicated environment to go there.
 * The dialog appears when they open the app anywhere else.
 * A choice to stay is remembered per organization in this browser.
 */
export const DedicatedEnvironmentDialog = memo(() => {
	const organization = useAppSelector(selectActiveOrganization)
	const [dismissedOrganizationUuids, setDismissedOrganizationUuids] = useState<ReadonlySet<Organization["uuid"]>>(
		() => new Set(),
	)

	const dedicatedUrl: URL | null = useMemo(() => {
		const url = parseUrl(organization?.enterprise_app_url)
		if (!url || url.protocol !== "https:" || url.origin === window.location.origin) return null
		return url
	}, [organization?.enterprise_app_url])

	// Pressing Escape and clicking "Stay here" both close the dialog. Both mean that the user stays.
	const handleOpenChange = useCallback(
		(nextOpen: boolean): void => {
			if (nextOpen || !organization) return
			setIsDismissed(organization.uuid)
			setDismissedOrganizationUuids((previous) => new Set(previous).add(organization.uuid))
		},
		[organization],
	)

	const handleGo = useCallback((): void => {
		if (!dedicatedUrl)
			throw new Error("The dialog is shown only when the organization has a dedicated environment.")
		window.location.assign(dedicatedUrl.origin)
	}, [dedicatedUrl])

	if (!organization || !dedicatedUrl) return null
	if (dismissedOrganizationUuids.has(organization.uuid) || getIsDismissed(organization.uuid)) return null

	return (
		<AlertDialog.Root open onOpenChange={handleOpenChange}>
			<AlertDialog.Content size="sm">
				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-1">
						<AlertDialog.Title>{organization.name} has an enterprise environment</AlertDialog.Title>
						<AlertDialog.Description asChild>
							<Text accentColor="base">
								You are currently using the public <code>{window.location.host}</code> app. You may be
								looking for the private {organization.name} environment at{" "}
								<a href={dedicatedUrl.origin}>{dedicatedUrl.host}</a>.
							</Text>
						</AlertDialog.Description>
					</div>

					<div className="flex justify-end gap-2">
						<AlertDialog.Cancel asChild>
							<Button variant="soft" accentColor="base" size="sm">
								Stay here
							</Button>
						</AlertDialog.Cancel>
						<Button variant="solid" size="sm" onClick={handleGo}>
							Go to {dedicatedUrl.host}
						</Button>
					</div>
				</div>
			</AlertDialog.Content>
		</AlertDialog.Root>
	)
})

DedicatedEnvironmentDialog.displayName = "DedicatedEnvironmentDialog"
